import React, { useEffect, useState } from 'react';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import NewspaperIcon from 'mdi-react/NewspaperIcon';
import CellphoneLinkIcon from 'mdi-react/CellphoneLinkIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import FormModal from '../../shared/components/custom/FormModal';
import ChoiceCards from '../../shared/components/custom/ChoiceCards';
import renderField from '../../shared/components/custom/Field';
import renderSelectField from '../../shared/components/form/Select';
import IconPicker from './IconPicker';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';

const p = partial('appLayout');
const s = partial('shared');
const FORM_NAME = 'more_link_form';
// As the backend allows (app-layout-service validate-layout.ts)
const MAX_LABEL_LENGTH = 40;

// What a link in the More menu opens, like a spotlight tile: a news article,
// a web page in the app's web view, or one in the in-app browser
export const LINK_TYPES = [
  {
    value: 'NEWS',
    label: p('moreLinkTypeNews'),
    icon: NewspaperIcon,
    hint: p('moreLinkTypeNewsHint'),
  },
  {
    value: 'WEBVIEW_LINK',
    label: s('webViewLink'),
    icon: CellphoneLinkIcon,
    hint: p('moreLinkTypeWebviewHint'),
  },
  {
    value: 'EXTERNAL_LINK',
    label: s('externalLink'),
    icon: OpenInNewIcon,
    hint: p('moreLinkTypeExternalHint'),
  },
];

const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const validate = (values) => {
  const errors = {};
  const label = (values.label || '').trim();
  if (!label) {
    errors.label = p('moreLinkNameMissing');
  } else if (label.length > MAX_LABEL_LENGTH) {
    errors.label = p('moreLinkNameTooLong', { max: MAX_LABEL_LENGTH });
  }
  if (!values.contentType) {
    errors.contentType = p('moreLinkTypeMissing');
  } else if (values.contentType === 'NEWS') {
    if (!values.article) {
      errors.article = p('moreLinkArticleMissing');
    }
  } else if (!isHttpsUrl((values.url || '').trim())) {
    errors.url = p('moreLinkUrlInvalid');
  }
  return errors;
};

let MoreLinkForm = ({
  link,
  contentType,
  label,
  articles,
  handleSubmit,
  initialize,
  change,
  onClose,
}) => {
  useEffect(() => {
    const isNews = link?.contentType === 'NEWS';
    initialize({
      label: link?.label || '',
      // A new link starts as a news article, the most common
      contentType: link?.contentType || 'NEWS',
      article: isNews
        ? articles.find((e) => e.value === link.contentId) || {
            // An older article than those listed: keep it as it is
            value: link.contentId,
            label: link.label,
          }
        : null,
      url: !isNews ? link?.contentId || '' : '',
      // '' = the app's default for the type
      icon: link?.icon || '',
    });
  }, [link]);

  return (
    // In the modal the form is a column: fields, then the buttons under them
    <form className="form qm-form" onSubmit={handleSubmit}>
      <div className="qm-body">
        <div className="qm-section">
          <span className="qm-label">{p('moreLinkType')}</span>
          <Field
            name="contentType"
            component={ChoiceCards}
            ariaLabel={p('moreLinkType')}
            columns={1}
            options={LINK_TYPES}
          />
        </div>
        {contentType === 'NEWS' && (
          <div className="form__form-group mb-0">
            <span className="form-header">{p('moreLinkArticle')}</span>
            <Field
              name="article"
              component={renderSelectField}
              options={articles}
              placeholder={p('moreLinkArticle')}
              onChange={(article) => {
                // The article's title as the name, unless one is given
                if (!link?.label && article?.label) {
                  change('label', article.label.slice(0, MAX_LABEL_LENGTH));
                }
              }}
            />
          </div>
        )}
        {contentType && contentType !== 'NEWS' && (
          <div className="form__form-group mb-0">
            <span className="form-header">{p('moreLinkUrl')}</span>
            <Field name="url" component={renderField} placeholder="https://" />
          </div>
        )}
        <div className="form__form-group mb-0">
          <span className="form-header more-link__name-head">
            {p('moreLinkName')}
            <span
              className={`more-link__count${
                (label || '').length > MAX_LABEL_LENGTH
                  ? ' more-link__count--over'
                  : ''
              }`}
            >
              {(label || '').length} / {MAX_LABEL_LENGTH}
            </span>
          </span>
          <Field
            name="label"
            component={renderField}
            placeholder={p('moreLinkName')}
          />
        </div>
        <div className="qm-section">
          <span className="qm-label">{p('iconField')}</span>
          <Field
            name="icon"
            component={IconPicker}
            ariaLabel={p('iconField')}
          />
        </div>
      </div>
      <div className="qm-footer">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0"
          onClick={onClose}
        >
          {p('cancel')}
        </button>
        <button type="submit" className="btn btn-primary btn-sm mb-0">
          {link ? s('update') : s('create')}
        </button>
      </div>
    </form>
  );
};

const selector = formValueSelector(FORM_NAME);
MoreLinkForm = connect((state) => ({
  contentType: selector(state, 'contentType'),
  label: selector(state, 'label'),
}))(reduxForm({ form: FORM_NAME, validate })(MoreLinkForm));

// Create or edit a link in the More menu. `link` is the one being edited
// ({ label, contentType, contentId, icon }), or null for a new one.
const MoreLinkModal = ({ isOpen, link, onClose, onSave }) => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    Api.news
      .getNews(
        { page: 1, take: 100 },
        { orderBy: 'publicationDate', direction: 'DESC' }
      )
      .then(({ data }) =>
        setArticles(data.map((item) => ({ label: item.title, value: item.id })))
      )
      .catch(() => setArticles([]));
  }, [isOpen]);

  const handleSubmit = (values) => {
    const isNews = values.contentType === 'NEWS';
    onSave({
      label: values.label.trim(),
      contentType: values.contentType,
      contentId: isNews ? values.article.value : values.url.trim(),
      icon: values.icon || '',
    });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={p('moreLinkTitle')}
      subtitle={p('moreLinkHint')}
    >
      <MoreLinkForm
        link={link}
        articles={articles}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </FormModal>
  );
};

export default MoreLinkModal;
